import React, { useState, useEffect, useMemo } from "react";
import { X, Search, ChevronLeft, ChevronRight, ArrowUpDown } from "lucide-react";
import api from "~/services/api";
import { toast } from "react-toastify";

export default function GenerateModal({
  opened = false,
  onClose = () => null,
  onSelectStaff = () => null,
  onTimesheetsGenerated = () => null,
}) {
  const [staffs, setStaffs] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedTerm, setDebouncedTerm] = useState("");
  const [filterEmployeeType, setFilterEmployeeType] = useState("");
  const [filterDepartment, setFilterDepartment] = useState("");
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 10;
  const [sortBy, setSortBy] = useState("name");
  const [sortOrder, setSortOrder] = useState("asc");
  const [selectedStaffIds, setSelectedStaffIds] = useState([]);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [periodFrom, setPeriodFrom] = useState("");
  const [periodTo, setPeriodTo] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [conflictInfo, setConflictInfo] = useState(null);
  const [showConflictModal, setShowConflictModal] = useState(false);

  const getFortnightEnd = (from) => {
    if (!from) return null;
    const start = new Date(from + "T00:00:00");
    if (Number.isNaN(start.getTime())) return null;
    const end = new Date(start);
    end.setDate(end.getDate() + 14);
    return end.toISOString().split("T")[0];
  };

  useEffect(() => {
    const t = setTimeout(() => setDebouncedTerm((searchTerm || "").trim().toLowerCase()), 300);
    return () => clearTimeout(t);
  }, [searchTerm]);

  useEffect(() => {
    setPage(1);
    setSelectedStaffIds([]);
  }, [debouncedTerm, filterEmployeeType, filterDepartment]);

  useEffect(() => {
    async function loadStaffs() {
      try {
        const { data } = await api.get("/staffs-simple");
        setStaffs(data || []);
        console.log("Staffs loaded:", data);
      } catch (err) {
        console.error("Error loading staffs:", err);
        toast.error("Failed to load staffs list");
      }
    }
    if (opened) {
      loadStaffs();
    }
  }, [opened]);

  const filteredStaffs = useMemo(() => {
    const term = debouncedTerm;
    let filtered = staffs.filter((staff) => {
      const matchSearch = !term || staff.name.toLowerCase().includes(term);
      const matchType = !filterEmployeeType || staff.employee_type === filterEmployeeType;
      const matchDepartment = !filterDepartment || (staff.department?.name || "").toLowerCase().includes(filterDepartment.toLowerCase());
      return matchSearch && matchType && matchDepartment;
    });

    filtered.sort((a, b) => {
      let aVal, bVal;
      if (sortBy === "name") {
        aVal = a.name || "";
        bVal = b.name || "";
      } else if (sortBy === "employee_type") {
        aVal = a.employee_type || "";
        bVal = b.employee_type || "";
      } else if (sortBy === "department") {
        aVal = a.department?.name || "";
        bVal = b.department?.name || "";
      }

      if (sortOrder === "asc") {
        return aVal.localeCompare(bVal);
      } else {
        return bVal.localeCompare(aVal);
      }
    });

    return filtered;
  }, [staffs, debouncedTerm, filterEmployeeType, filterDepartment, sortBy, sortOrder]);

  const totalPages = Math.max(1, Math.ceil((filteredStaffs?.length || 0) / PAGE_SIZE));
  const pageStaffs = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return filteredStaffs.slice(start, start + PAGE_SIZE);
  }, [filteredStaffs, page]);

  const handleSelectStaff = (staff) => {
    onSelectStaff(staff);
    handleClose();
  };

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(column);
      setSortOrder("asc");
    }
  };

  const handleToggleStaff = (staffId) => {
    setSelectedStaffIds((prev) => {
      if (prev.includes(staffId)) {
        return prev.filter((id) => id !== staffId);
      } else {
        return [...prev, staffId];
      }
    });
  };

  const handleToggleAll = () => {
    const allIds = filteredStaffs.map((s) => s.id);
    if (allIds.length === 0) return;
    const allSelected = allIds.every((id) => selectedStaffIds.includes(id));
    if (allSelected) {
      setSelectedStaffIds((prev) => prev.filter((id) => !allIds.includes(id)));
    } else {
      setSelectedStaffIds((prev) => {
        const merged = new Set([...prev, ...allIds]);
        return Array.from(merged);
      });
    }
  };

  const handleGenerate = () => {
    if (selectedStaffIds.length === 0) {
      toast.error("Please select at least one staff member");
      return;
    }
    if (!periodFrom || !periodTo) {
      toast.error("Please select period from and to dates");
      return;
    }
    setConflictInfo(null);
    setShowConflictModal(false);
    setShowConfirmation(true);
  };

  const performGeneration = async (options = {}) => {
    const { forceOverride = false, statusOverride = "draft" } = options;
    setIsGenerating(true);
    try {
      const payload = {
        staff_ids: selectedStaffIds,
        force_override: forceOverride,
      };

      const { data } = await api.post("/get-timesheet-staffs", payload, {
        params: {
          from: periodFrom,
          to: periodTo,
          status: statusOverride,
        },
      });

      console.log("Timesheets generated:", data);
      toast.success(
        `Timesheets generated successfully for ${selectedStaffIds.length} staff member${
          selectedStaffIds.length !== 1 ? "s" : ""
        }!`
      );
      onSelectStaff(data);
      onTimesheetsGenerated(); // Callback to refresh datatable
      setShowConfirmation(false);
      handleClose();
    } catch (err) {
      console.error("Error generating timesheets:", err);
      if (
        err?.response?.status === 409 &&
        err?.response?.data?.code === "TIMESHEET_PERIOD_CONFLICT"
      ) {
        setShowConfirmation(false);
        setConflictInfo(err.response.data);
        setShowConflictModal(true);
        toast.warn("Conflicts detected for the selected period. Review them below.");
        return;
      }
      if (
        err?.response?.status === 409 &&
        err?.response?.data?.code === "TIMESHEET_PERIOD_LOCKED"
      ) {
        setShowConfirmation(false);
        setConflictInfo(err.response.data);
        setShowConflictModal(true);
        toast.error("Cannot override submitted/approved/rejected timesheets.");
        return;
      }
      if (err?.response?.data?.error) {
        toast.error(err.response.data.error);
      } else {
        toast.error("Failed to generate timesheets");
      }
    } finally {
      setIsGenerating(false);
    }
  };

  const handleConfirmGenerate = () => {
    performGeneration();
  };

  const handleConfirmOverride = () => {
    const statusOverride = conflictInfo?.newTimesheetStatusOnOverride || "draft";
    setShowConflictModal(false);
    performGeneration({
      forceOverride: true,
      statusOverride,
    });
  };

  const handleClose = () => {
    setSearchTerm("");
    setFilterEmployeeType("");
    setFilterDepartment("");
    setPage(1);
    setSelectedStaffIds([]);
    setShowConfirmation(false);
    setPeriodFrom("");
    setPeriodTo("");
    setConflictInfo(null);
    setShowConflictModal(false);
    onClose();
  };

  if (!opened) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: "rgba(0,0,0,.75)" }}>
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden">
        <div className="flex items-center justify-between p-4 border-b flex-shrink-0">
          <div className="flex-1">
            <h2 className="text-xl font-bold">Select Staff Member</h2>
            <p className="text-sm text-gray-600 mt-1">
              Choose staff members to create their timesheets
            </p>
          </div>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors flex-shrink-0"
          >
            <X size={24} />
          </button>
        </div>

        <div className="p-4 border-b bg-gray-50 flex-shrink-0">
          <div className="flex flex-col gap-3">
            <div className="flex items-center gap-3">
              <div className="flex flex-col gap-1">
                <label className="text-xs font-semibold text-gray-600">Period From</label>
                <input
                  type="date"
                  value={periodFrom}
                  onChange={(e) => {
                    const newFrom = e.target.value;
                    setPeriodFrom(newFrom);
                    const autoTo = getFortnightEnd(newFrom);
                    if (autoTo) {
                      setPeriodTo(autoTo);
                    }
                  }}
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-hidden focus:ring-2 focus:ring-orange-500"
                />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-xs font-semibold text-gray-600">Period To</label>
                <input
                  type="date"
                  value={periodTo}
                  onChange={(e) => setPeriodTo(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-hidden focus:ring-2 focus:ring-orange-500"
                />
              </div>
            </div>

            <div className="flex items-center gap-3 text-sm text-gray-700">
              <span>
                Showing <span className="font-semibold">{filteredStaffs.length}</span> of <span className="font-semibold">{staffs.length}</span> staff members
              </span>
              {selectedStaffIds.length > 0 && (
                <span className="px-2 py-1 bg-orange-100 text-orange-700 rounded-md font-semibold">
                  {selectedStaffIds.length} selected
                </span>
              )}
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3 mt-3">
            <div className="flex items-center gap-2">
              <div className="relative">
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search by name..."
                  className="w-72 pl-10 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-hidden focus:ring-2 focus:ring-orange-500"
                />
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
              </div>
              <select
                value={filterEmployeeType}
                onChange={(e) => setFilterEmployeeType(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-hidden focus:ring-2 focus:ring-orange-500"
              >
                <option value="">All Types</option>
                <option value="Faculty">Faculty</option>
                <option value="Staff">Staff</option>
              </select>
              <input
                type="text"
                value={filterDepartment}
                onChange={(e) => setFilterDepartment(e.target.value)}
                placeholder="Department..."
                className="w-48 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-hidden focus:ring-2 focus:ring-orange-500"
              />
              <button
                type="button"
                onClick={handleGenerate}
                disabled={isGenerating}
                className="px-4 py-2 bg-mila_orange hover:bg-orange-600 text-white font-semibold rounded-lg text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isGenerating ? "Generating..." : "Generate"}
              </button>
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-auto min-h-0">
          <table className="w-full border-collapse">
            <thead className="sticky top-0 bg-gray-100 z-0 shadow-sm">
              <tr className="border-b-2 border-gray-300">
                <th className="text-center p-3 text-sm font-semibold text-gray-700 w-12">
                  <input
                    type="checkbox"
                    checked={
                      filteredStaffs.length > 0 &&
                      filteredStaffs.every((s) => selectedStaffIds.includes(s.id))
                    }
                    onChange={handleToggleAll}
                    className="w-4 h-4 cursor-pointer accent-orange-500"
                  />
                </th>
                <th
                  className="text-left p-3 text-sm font-semibold text-gray-700 cursor-pointer hover:bg-gray-200 transition-colors"
                  onClick={() => handleSort("name")}
                >
                  <div className="flex items-center gap-2">
                    Name
                    <ArrowUpDown size={14} className={sortBy === "name" ? "text-orange-500" : "text-gray-400"} />
                  </div>
                </th>
                <th
                  className="text-left p-3 text-sm font-semibold text-gray-700 cursor-pointer hover:bg-gray-200 transition-colors"
                  onClick={() => handleSort("employee_type")}
                >
                  <div className="flex items-center gap-2">
                    Employee Type
                    <ArrowUpDown size={14} className={sortBy === "employee_type" ? "text-orange-500" : "text-gray-400"} />
                  </div>
                </th>
                <th
                  className="text-left p-3 text-sm font-semibold text-gray-700 cursor-pointer hover:bg-gray-200 transition-colors"
                  onClick={() => handleSort("department")}
                >
                  <div className="flex items-center gap-2">
                    Department
                    <ArrowUpDown size={14} className={sortBy === "department" ? "text-orange-500" : "text-gray-400"} />
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              {pageStaffs?.map((staff, idx) => (
                <tr
                  key={staff.id}
                  className={`border-b transition-colors ${idx % 2 === 0 ? "bg-white" : "bg-gray-50"} hover:bg-orange-50`}
                >
                  <td className="p-3 text-center">
                    <input
                      type="checkbox"
                      checked={selectedStaffIds.includes(staff.id)}
                      onChange={() => handleToggleStaff(staff.id)}
                      onClick={(e) => e.stopPropagation()}
                      className="w-4 h-4 cursor-pointer accent-orange-500"
                    />
                  </td>
                  <td className="p-3 text-sm text-gray-700 whitespace-nowrap">{staff.name}</td>
                  <td className="p-3 text-sm text-gray-700 whitespace-nowrap">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      staff.employee_type === "Faculty"
                        ? "bg-blue-100 text-blue-700"
                        : "bg-green-100 text-green-700"
                    }`}>
                      {staff.employee_type || "N/A"}
                    </span>
                  </td>
                  <td className="p-3 text-sm text-gray-700 whitespace-nowrap">{staff.department?.name || "-"}</td>
                </tr>
              ))}
              {(!pageStaffs || pageStaffs.length === 0) && (
                <tr>
                  <td colSpan="4" className="p-4 text-center text-gray-500 text-sm">
                    {debouncedTerm || filterEmployeeType || filterDepartment
                      ? "No staff members found matching the filters"
                      : "No staff members available"}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="p-4 border-t flex items-center justify-between bg-gray-50 text-sm flex-shrink-0">
          <div className="text-sm text-gray-600">Page {page} of {totalPages}</div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="p-2 rounded-lg border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 transition-colors bg-white"
            >
              <ChevronLeft size={20} />
            </button>
            <span className="text-sm px-3 font-medium text-gray-700">
              {page} / {totalPages}
            </span>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="p-2 rounded-lg border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 transition-colors bg-white"
            >
              <ChevronRight size={20} />
            </button>
          </div>
        </div>
      </div>

      {showConfirmation && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" style={{ backgroundColor: "rgba(0,0,0,.85)" }}>
          <div className="bg-white rounded-lg shadow-2xl w-full max-w-md p-6">
            <h3 className="text-lg font-bold text-gray-800 mb-4">Confirm Generation</h3>
            <p className="text-sm text-gray-600 mb-2">
              Are you sure you want to create timesheet for{" "}
              <span className="font-bold text-orange-600">{selectedStaffIds.length}</span>{" "}
              user{selectedStaffIds.length !== 1 ? "s" : ""} selected?
            </p>
            <div className="text-sm text-gray-600 mb-6 bg-gray-50 p-3 rounded-lg">
              <p>
                <span className="font-semibold">Period:</span>{" "}
                {periodFrom && periodTo ? (
                  <>
                    <span className="font-mono">{periodFrom}</span> to{" "}
                    <span className="font-mono">{periodTo}</span>
                  </>
                ) : (
                  "Not specified"
                )}
              </p>
            </div>
            <div className="flex gap-3 justify-end">
              <button
                type="button"
                onClick={() => setShowConfirmation(false)}
                className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 font-semibold rounded-lg text-sm transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmGenerate}
                disabled={isGenerating}
                className="px-4 py-2 bg-mila_orange hover:bg-orange-600 text-white font-semibold rounded-lg text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isGenerating ? "Generating..." : "Confirm"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showConflictModal && conflictInfo && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" style={{ backgroundColor: "rgba(0,0,0,.85)" }}>
          <div className="bg-white rounded-lg shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden">
            <div className="flex items-start justify-between p-4 border-b">
              <div className="pr-4">
                <h3 className="text-lg font-bold text-gray-800">Conflicting timesheets detected</h3>
                {conflictInfo?.allowOverride ? (
                  <p className="text-sm text-gray-600 mt-1">
                    There are existing timesheets overlapping the selected period. Review and confirm to override.
                  </p>
                ) : (
                  <p className="text-sm text-red-600 mt-1">
                    Cannot override submitted/approved/rejected timesheets. Please resolve these conflicts before generating.
                  </p>
                )}
              </div>
              <button
                onClick={() => {
                  setShowConflictModal(false);
                  setConflictInfo(null);
                }}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            <div className="p-4 flex-1 overflow-auto space-y-3">
              <div className="bg-gray-50 border border-gray-200 rounded-md p-3 text-sm text-gray-700">
                <p>
                  <span className="font-semibold">Period:</span>{" "}
                  <span className="font-mono">{periodFrom || "-"}</span> to{" "}
                  <span className="font-mono">{periodTo || "-"}</span>
                </p>
              </div>

              {conflictInfo?.conflicts?.map((staffConflict) => (
                <div key={staffConflict.staff_id} className="border border-orange-200 rounded-md overflow-hidden">
                  <div className="bg-orange-50 px-3 py-2 flex items-center justify-between">
                    <div>
                      <p className="text-sm font-semibold text-orange-800">
                        {staffConflict.staff?.name || "Unknown staff"}
                      </p>
                      <p className="text-xs text-gray-700">
                        {staffConflict.staff?.email || "-"}
                      </p>
                    </div>
                    <span className="text-[10px] uppercase font-bold text-orange-700 bg-white border border-orange-200 rounded-full px-2 py-1">
                      {staffConflict.conflicts?.length || 0} conflict
                      {staffConflict.conflicts?.length === 1 ? "" : "s"}
                    </span>
                  </div>
                  <div className="divide-y divide-orange-100">
                    {staffConflict.conflicts?.map((c) => (
                      <div key={c.id} className="px-3 py-2 flex items-center justify-between text-sm">
                        <div>
                          <p className="font-semibold text-gray-800">Timesheet</p>
                          <p className="text-xs text-gray-700">
                            {c.period_from} — {c.period_to}
                          </p>
                        </div>
                        <span className="text-xs font-semibold px-2 py-1 rounded-full bg-orange-100 text-orange-800 uppercase border border-orange-200">
                          {c.status}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}

              {conflictInfo?.newTimesheetStatusOnOverride && (
                <div className="text-xs text-gray-700 bg-blue-50 border border-blue-200 rounded-md p-3">
                  On override, conflicting timesheets will be marked as{" "}
                  <span className="font-semibold">{conflictInfo.overrideStatus || "override"}</span>{" "}
                  and new ones will be created as{" "}
                  <span className="font-semibold">{conflictInfo.newTimesheetStatusOnOverride}</span>.
                </div>
              )}
            </div>

            <div className="p-4 border-t flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  setShowConflictModal(false);
                  setConflictInfo(null);
                }}
                className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 font-semibold rounded-lg text-sm transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmOverride}
                disabled={!conflictInfo?.allowOverride || isGenerating}
                className="px-4 py-2 bg-mila_orange hover:bg-orange-600 text-white font-semibold rounded-lg text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isGenerating ? "Overriding..." : "Override and generate"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
